// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useState } from 'react';
import moment from 'moment-timezone';
import { Box, useTheme } from '@mui/system';
import { Typography, Divider, Button, Fade, CircularProgress, TextField, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { RootState } from 'Store';
import { GetOrgThunk, UpdateLicenseFeeThunk } from 'Features/admin/adminThunk';
import { licenseFeeModes, prepaidCadences } from 'Utils/constants';
import stylesDef from './styles';

moment.tz.setDefault('America/New_York');

// The three states in the server's words, with the cadence for a prepaid license: "prepaid (annual)"
export const describeSetting = (mode?: string, prepaidCadence?: string | null) => {
  if (!mode) return 'none';
  return mode === 'prepaid' && prepaidCadence ? `${mode} (${prepaidCadence})` : mode;
};

const modeOption = (mode?: string) => licenseFeeModes.find((option) => option.value === mode);
const cadenceLabel = (cadence?: string | null) => prepaidCadences.find((option) => option.value === cadence)?.label;

/**
 * The account's one license-fee setting (AUT-6569): what each order's price does about the 10% license fee. One
 * setting for AEC and catalog orders alike; set here, with a note, by admins with pricing access. A pay-as-you-go
 * account (no plan commitment, no committed tier) never carries one, so its row is locked at "No license fee".
 */
export const LicenseFee = () => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();

  const { user } = useAppSelector((state: RootState) => state.auth);
  const { org } = useAppSelector((state: RootState) => state.admin);
  const current = org?.licenseFee || { mode: 'none', prepaidCadence: null };
  const history = org?.licenseFeeHistory || [];
  const payAsYouGo = org?.currentPricingPlanInfo?.name === 'no_commit' && !org?.currentCommittedTierInfo;

  const [editing, setEditing] = useState(false);
  const [mode, setMode] = useState<string>(current.mode);
  const [cadence, setCadence] = useState<string>(current.prepaidCadence || 'annual');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const unchanged = mode === current.mode && (mode !== 'prepaid' || cadence === current.prepaidCadence);
  const canSave = !!note.trim() && !unchanged && !saving;

  const openEdit = () => {
    setMode(current.mode);
    setCadence(current.prepaidCadence || 'annual');
    setNote('');
    setError('');
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setError('');
  };

  const save = async () => {
    setSaving(true);
    setError('');
    const result = await dispatch(UpdateLicenseFeeThunk(mode, mode === 'prepaid' ? cadence : null, note.trim()));
    if (result.success) {
      await dispatch(GetOrgThunk(org._id));
      setEditing(false);
    } else {
      setError(result.message || 'Failed to update the license fee');
    }
    setSaving(false);
  };

  const currentOption = modeOption(current.mode);
  const currentLabel = current.mode === 'prepaid' && current.prepaidCadence
    ? `${currentOption?.label} (${cadenceLabel(current.prepaidCadence)})`
    : currentOption?.label;

  return (
    <Box>
      <Typography variant="h4" sx={styles.sectionTitle}>License Fee</Typography>
      <Divider sx={styles.divider} />
      <Box sx={styles.listContent}>
        <Typography>License fee</Typography>
        <Typography>{payAsYouGo ? 'No license fee' : currentLabel}</Typography>
      </Box>
      <Typography variant="body2" sx={styles.helperText}>
        {payAsYouGo ? 'Pay-as-you-go accounts pay list with no license fee.' : currentOption?.description}
      </Typography>
      {!payAsYouGo && !editing && (
        <Box sx={styles.planButtonGroup}>
          <Button disabled={!user?.pricingAccess} sx={styles.planButton} onClick={openEdit} color="primary" variant="outlined">
            Change
          </Button>
        </Box>
      )}
      {editing && (
        <Box sx={styles.form}>
          <RadioGroup aria-label="License fee" value={mode} onChange={(e) => setMode(e.target.value)}>
            {licenseFeeModes.map((option) => (
              <React.Fragment key={option.value}>
                <FormControlLabel value={option.value} control={<Radio />} label={option.label} />
                <Typography variant="body2" sx={styles.optionDescription}>{option.description}</Typography>
                {option.value === 'prepaid' && mode === 'prepaid' && (
                  <RadioGroup row aria-label="Prepaid cadence" value={cadence} onChange={(e) => setCadence(e.target.value)} sx={styles.cadenceGroup}>
                    {prepaidCadences.map((cadenceOption) => (
                      <FormControlLabel key={cadenceOption.value} value={cadenceOption.value} control={<Radio size="small" />} label={cadenceOption.label} />
                    ))}
                  </RadioGroup>
                )}
              </React.Fragment>
            ))}
          </RadioGroup>
          <TextField
            id="licenseFeeNote"
            label="Note (required)"
            variant="standard"
            multiline
            minRows={2}
            onChange={(e) => setNote(e.target.value)}
            value={note}
            sx={styles.notesField}
          />
          {error && <Typography variant="body2" sx={styles.error}>{error}</Typography>}
          <Box sx={styles.buttonGroup}>
            <Button sx={styles.planButton} disabled={!canSave} onClick={save} color="primary" variant="contained">
              <Fade in={saving}>
                <CircularProgress size={24} sx={styles.buttonProgress} />
              </Fade>
              <Fade in={!saving}>
                <span>Save</span>
              </Fade>
            </Button>
            <Button sx={styles.planButton} onClick={cancelEdit} variant="text">Cancel</Button>
          </Box>
        </Box>
      )}
      {history.length > 0 && (
        <Box sx={styles.historyList}>
          {history.map((entry) => (
            <Box key={entry._id} sx={styles.historyRow}>
              <Typography variant="body2">{moment.utc(entry.createdAt).format('MM/DD/YYYY')}</Typography>
              <Typography variant="body2" sx={styles.historyChange}>
                {`${describeSetting(entry.previousMode, entry.previousCadence)} → ${describeSetting(entry.mode, entry.prepaidCadence)}`}
              </Typography>
              <Typography variant="body2">{entry.changedBy}</Typography>
              <Typography variant="body2" sx={styles.historyNotes}>{entry.note}</Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default LicenseFee;
